import cx from 'classnames';
import { History, Location } from 'history';
import isNumber from 'lodash-es/isNumber';
import range from 'lodash-es/range';
import { WIKI_REVIEWS } from 'mk/autogenerated/translations/GroupFeedPosts.caaa50e4675080744e3737f162c8820e'
import BlogPost from 'mk2/apps/blogs/components/BlogPost/BlogPost';
import { CommentForumPlaceholder } from 'mk2/apps/blogs/components/Comment';
import HashtagsBox, { BoxedHashtags } from 'mk2/apps/blogs/components/HashtagsBox';
import { getGroupHashtagLink } from 'mk2/apps/blogs/utils';
import { PageRef } from 'mk2/apps/forum/schemas';
import { SAME_DATE_GROUP_CATEGORY_ID } from 'mk2/apps/groups/containers/GroupFeed/GroupFeed';
import ReviewArticles, { LayoutType } from 'mk2/apps/wiki/components/ReviewArticles';
import { WIKI_REVIEWS_ARTICLE_SLUG } from 'mk2/apps/wiki/helpers';
import { Pager } from 'mk2/components/Pager';
import { AdSlot } from 'mk2/containers/AdSlot/AdSlot';
import Complaint from 'mk2/containers/Complaint/Complaint';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import Deletion from 'mk2/containers/Deletion/Deletion';
import { DeletionTargetType } from 'mk2/containers/Deletion/Deletion.schemas';
import { LoadingSwitch } from 'mk2/containers/LoadingSwitch/LoadingSwitch';
import { UserAgentParsed } from 'mk2/helpers/detects';
import { LoadingState } from 'mk2/reducers/loadingState';
import {
    BlogPostEntity,
    GroupEntity,
    UsersByGroupId,
    UserEntity,
    WikiArticleEntity,
} from 'mk2/schemas';
import React from 'react';
import { StaticContext } from 'react-router';
import styles from './GroupFeedPosts.mscss';


// based on ADSLOT_INDEXES
type PageItemType = 'adslot1' | 'adslot2' | 'adslot3' | 'adslot4' | 'hashtags' | 'post';

interface PageItem {
    type: PageItemType;
    item?: any;
    commentsCount?: number;
    complaintsCount?: number;
    isComplaintOpened?: boolean;
}

interface OwnProps {
    commentsCounts: { [postId: number]: number };
    complaintsCounts: { [postId: number]: number };
    complaintsOpened: boolean[];
    errorMessage: string;
    group?: GroupEntity;        // used for HashtagsBox
    hashtags?: BoxedHashtags;
    history: History;
    imageComplaintsCounts?: { [imageId: number]: number };
    loadingState: LoadingState;
    location: Location;
    isMobile: boolean;
    moderatorUsersByGroupId: UsersByGroupId;
    numberOfPages: number;
    pageRef: PageRef;
    permissions: string[];
    posts: BlogPostEntity[];
    feedLoaded: boolean;
    requestUser: UserEntity;
    showGroup?: boolean;
    showHashtagsBox: boolean;
    showStrips: boolean;
    staticContext: StaticContext;
    userAgent: UserAgentParsed;

    isAdmin: boolean;

    promoReviewArticlesMiddle?: WikiArticleEntity[];

    onChangeSticky(postId: number, sticky: boolean);
    onDelete?(postId: number);
    onGroupLeave?(groupId: number);
    onHideGroupInFeed?(groupId: number);
    onPostToggleMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number);
}

export const NO_BRANDING_AFTER = '2023-01';

type Props = OwnProps;

class GroupFeedPosts extends React.PureComponent<Props> {

    public render() {
        const { loadingState, feedLoaded } = this.props;

        return (
            <LoadingSwitch
                loadingState={loadingState}
                onLoad={this.dummyLoad}
                onRenderInit={this.renderInit}
                onRenderSuccess={this.renderSuccess}
                hasData={feedLoaded}
            />
        );
    }

    private renderInit = () => {
        const { isMobile, pageRef, numberOfPages } = this.props;
        const hasPager = numberOfPages !== null && numberOfPages > 1 && pageRef !== null && isNumber(pageRef);

        return (
            <div>
                <span id="topPager" />

                {// vykreslit dostatok elementov, tak aby browser nezmensil scrollTop position
                    // pocas LoadingState.LOADING pretoze vyska body je mensia nez scrollTop pozicia
                    // ktoru sa snazime zachovat
                    range(10).map((i) => (
                        <CommentForumPlaceholder
                            key={`placeholder-${i}`}
                            className={cx(
                                styles.GroupFeedPosts__commentPlaceholder,
                                i === 0 && styles['GroupFeedPosts__commentPlaceholder--hasAbove-Post'],
                            )}
                        />
                    ))}

                {hasPager && (
                    <Pager
                        id="bottomPager"
                        className={cx(styles.GroupFeedPosts__pager, styles['GroupFeedPosts__pager--bottom'])}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="bottom"
                        history={this.props.history}
                        isMobile={isMobile}
                    />
                )}
            </div>
        );
    };

    private renderSuccess = () => {
        const {
            hashtags, imageComplaintsCounts, location, history, isMobile, moderatorUsersByGroupId, numberOfPages, onChangeSticky,
            onDelete, onGroupLeave, onHideGroupInFeed, onPostToggleMenuMore,
            onReport, pageRef, permissions, posts, requestUser, showGroup, showStrips, userAgent, group,
            promoReviewArticlesMiddle = [],
        } = this.props;

        const hasPager = numberOfPages > 1 && isNumber(pageRef);
        const pageItems = this.getPageItems();

        if (posts.length === 0) {
            return null;
        }

        return (
            <React.Fragment>
                <span id="topPager" />

                {pageItems.map((pageItem, idx) => {
                    return (pageItem.type === 'post') ? (
                        <div
                            key={idx}
                            className={cx(
                                styles.GroupFeedPosts__post,
                                pageItem.isComplaintOpened && styles['GroupFeedPosts__post--complaintOpened'],
                            )}
                        >
                            <Complaint
                                id={pageItem.item.id}
                                entity={ComplaintTargetType.PhotoblogPost}
                                className={styles.GroupFeedPosts__post__complaint}
                                scrollOffset={44 + 44 + 30/* header + 30px spacing */}
                                inForum={false}
                                inGroups
                            />

                            <Deletion
                                id={pageItem.item.id}
                                entity={DeletionTargetType.PhotoblogPost}
                                className={styles.GroupFeedPosts__post__complaint}
                                scrollOffset={44 + 44 + 30/* header + 30px spacing */}
                            />

                            <BlogPost
                                post={pageItem.item}
                                className={cx(
                                    pageItem.isComplaintOpened && styles['GroupFeedPosts__post__blogPost--complaintOpened'],
                                )}
                                isMobile={isMobile}
                                commentsCount={pageItem.commentsCount}
                                complaintsCount={pageItem.complaintsCount}
                                groupModerators={moderatorUsersByGroupId[(pageItem.item as BlogPostEntity).group.id]}
                                imageComplaintsCounts={imageComplaintsCounts}
                                requestUser={requestUser}
                                requestUserPermissions={permissions}
                                location={location}
                                history={history}
                                onToggleMenuMore={onPostToggleMenuMore}
                                onChangeSticky={onChangeSticky}
                                onDelete={onDelete}
                                onGroupLeave={onGroupLeave}
                                onHideGroupInFeed={onHideGroupInFeed}
                                onReport={onReport}
                                showGroup={showGroup}
                                showSticky={!showGroup}
                                showStrip={showStrips}
                                userAgent={userAgent}
                            />
                            {promoReviewArticlesMiddle.length > 0 && idx === 13 && group &&
                                <ReviewArticles
                                    headerLogo
                                    header={WIKI_REVIEWS}
                                    headerLinkTo={`/${WIKI_REVIEWS_ARTICLE_SLUG}/?utm_medium=widget`}
                                    articles={promoReviewArticlesMiddle}
                                    layout={LayoutType.COMPACT}
                                    utmParams={{
                                        utm_medium: 'widget',
                                    }}
                                />
                            }
                        </div>
                    ) : (pageItem.type === 'hashtags') ? (
                        // show boxed hashtags
                        <HashtagsBox
                            key={idx}
                            hashtags={hashtags}
                            createLink={this.hashtagLinkCreate}
                        />
                    ) : (pageItem.type === 'adslot1' && isMobile) ? (
                        <AdSlot
                            key={idx}
                            zone="Groups Feed - Halfpage (Mobile)"
                            location={location}
                            marginTopWithAd={10}
                        />
                  ) : (((pageItem.type === 'adslot2' && isMobile) && (group && group.categoryId !== SAME_DATE_GROUP_CATEGORY_ID))
                        || (group && group.categoryId === SAME_DATE_GROUP_CATEGORY_ID && group.domainMonth < NO_BRANDING_AFTER)) ? (
                        <AdSlot
                            key={idx}
                            zone="Groups Feed - Rectangle 4 (Mobile)"
                            location={location}
                            marginTopWithAd={10}
                        />
                    ) : (((pageItem.type === 'adslot3' && isMobile) && (group && group.categoryId !== SAME_DATE_GROUP_CATEGORY_ID))
                        || (group && group.categoryId === SAME_DATE_GROUP_CATEGORY_ID && group.domainMonth < NO_BRANDING_AFTER)) ? (
                        <AdSlot
                            key={idx}
                            zone="Groups Feed - Rectangle 5 (Mobile)"
                            location={location}
                            marginTopWithAd={10}
                        />
                    ) : (((pageItem.type === 'adslot4' && isMobile) && (group && group.categoryId !== SAME_DATE_GROUP_CATEGORY_ID))
                        || (group && group.categoryId === SAME_DATE_GROUP_CATEGORY_ID && group.domainMonth < NO_BRANDING_AFTER)) ? (
                        <AdSlot
                            key={idx}
                            zone="Groups Feed - Rectangle 6 (Mobile)"
                            location={location}
                            marginTopWithAd={10}
                        />
                    ) : null;
                })}

                {hasPager && (
                    <Pager
                        id="bottomPager"
                        className={cx(styles.GroupFeedPosts__pager, styles['GroupFeedPosts__pager--bottom'])}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="bottom"
                        history={this.props.history}
                        isMobile={isMobile}
                    />
                )}
            </React.Fragment>
        );
    };

    private dummyLoad = () => {
        return null;
    };

    private getPageItems = (): PageItem[] => {
        const ADSLOT_INDEXES = [2, 9, 16, 23];
        const HASHTAGS_BOX_INDEX = 6;

        const {
            commentsCounts, complaintsCounts, complaintsOpened, group, hashtags, posts, showHashtagsBox,
        } = this.props;

        const items: PageItem[] = posts.map((post, idx): PageItem => ({
            type: 'post',
            item: post,
            commentsCount: commentsCounts[post.id],
            complaintsCount: complaintsCounts[post.id],
            isComplaintOpened: complaintsOpened[idx],
        }));

        // 2 posty - reklama - 7 postov - reklama - 7 postov - reklama - 7 postov - reklama - 2 posty
        // adslot vlozime, ak za nim budu nasledovat este minimalne 2 posty (nech nekoncime reklamou)
        if (!group || (group && !group.isNoAds)) {
            ADSLOT_INDEXES.forEach((idx, i) => {
                if (posts.length >= idx + 2) {
                    items.splice(idx + i /* +i for previous inserts */, 0, { type: `adslot${i + 1}` as PageItemType });
                }
            });
        }

        if (showHashtagsBox && hashtags.newest.length > 0) {
            items.splice(HASHTAGS_BOX_INDEX, 0, { type: 'hashtags' });
        }

        return items;
    };

    private hashtagLinkCreate = (hashtag: string) => {
        const { group } = this.props;
        return getGroupHashtagLink(group.id, hashtag);
    };
}

export default GroupFeedPosts;
